import React, { createContext, useContext, useState, useEffect } from "react";
import useGenerateProcess from "hooks/api/process/useGenerateProcess";
import useGetProcessResult from "hooks/api/process/useGetProcessResult";
import useProcessStatusPolling from "hooks/api/process/useProcessStatusPolling";
import GlobalMira from "elements/GlobalMira";

export const MiraProcessSuccessStates = ["completed", "ready"];
export const MiraProcessFailedStates = ["aborted", "failed", "error"];
export const MiraProcessRunningStates = ["in_progress", "running", "pending"];
export const MiraProcessTerminalStates = [...MiraProcessFailedStates, ...MiraProcessSuccessStates];
export const MiraProcessStates = [...MiraProcessTerminalStates, ...MiraProcessRunningStates];

/**
 * Context for managing global Mira functionality.
 * Provides a hook to access the `showMira` function and other stateful operations.
 */
const MiraContext = createContext();

export function parseProcessStatus(data) {
  // TODO formalize states (create api/web shared NPM lib for constants, biz logic, etc.)
  // TODO create consistent response structs
  let status = "unknown";

  if (MiraProcessStates.includes(data?.data?.status)) {
    status = data.data.status;
  }
  if (MiraProcessStates.includes(data?.data)) {
    status = data.data;
  }
  if (MiraProcessStates.includes(data?.status)) {
    status = data.status;
  }
  if (MiraProcessStates.includes(data)) {
    status = data;
  }

  return status;
}

/**
 * Custom hook to access the global Mira functionality.
 * @returns {Object} - An object containing `showMira`, `hideMira`, `generate`, and `miraState`.
 * @throws {Error} - If the hook is used outside of a `GlobalMiraProvider`.
 */
export const useMira = () => {
  const context = useContext(MiraContext);

  if (!context) {
    throw new Error("useMira must be used within a MiraProvider");
  }

  const { showMira, hideMira, generate, miraState, setMiraReadyState } = context;
  return { showMira, hideMira, generate, miraState, setMiraReadyState };
};

/**
 * Provider component to manage global Mira state and provide functionality to child components.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 */
export const GlobalMiraProvider = ({ children }) => {
  // Default UI properties for GlobalMira
  const defaultProps = {
    open: false,
    retryHandler: null,
    userInput: "",
    buttonText: "Generate",
    copyToClipboard: true,
    customButton: null,
    showTextArea: true,
    showOutputArea: true,
  };

  // Initial Mira state configuration enabling the functionality to make api calls
  const miraReadyState = {
    processIsComplete: false,
    processStatus: "ready",
    response: "",
    invocationId: null,
    generationAttemptId: null,
    correlationReceipt: null,
    error: false,
    targetProcess: "",
    processData: null,
    pollingDelay: 0,
    pollingDelayComplete: false,
  };
  // State to manage GlobalMira UI properties
  const [globalMiraProps, setGlobalMiraProps] = useState({
    ...defaultProps,
  });

  // Separate state to manage Mira's API interactions and processing status
  const [miraState, setMiraState] = useState({
    ...miraReadyState,
  });

  /**
   * Opens the Mira modal with customizable properties.
   * @param {Object} options - Configuration options for GlobalMira UI.
   * @param {Function|null} [options.retryHandler=null] - Function to retry the action in case of an error.
   * @param {string} [options.userInput=""] - User input to display in the input text box.
   * @param {string} [options.buttonText="Generate"] - Text displayed on the action button in GlobalMira.
   * @param {boolean} [options.copyToClipboard=true] - Whether to show the Copy to Clipboard button.
   * @param {React.ReactNode|null} [options.customButton=null] - Custom button component that overrides the default button.
   * @param {boolean} [options.showTextArea=true] - Whether to display the input text area.
   * @param {boolean} [options.showOutputArea=true] - Whether to display the response area.
   */
  const showMira = ({
    retryHandler = null,
    userInput = "",
    buttonText = "Generate",
    copyToClipboard = true,
    customButton = null,
    showTextArea = true,
    showOutputArea = true,
  }) => {
    setGlobalMiraProps({
      open: true,
      retryHandler,
      userInput,
      buttonText,
      copyToClipboard,
      customButton,
      showTextArea,
      showOutputArea,
    });
  };

  /**
   * Closes the Mira modal and resets its state.
   */
  const hideMira = () => {
    setGlobalMiraProps({
      ...defaultProps,
      open: false,
    });
    setMiraState({
      ...miraReadyState,
    });
  };

  /**
   * Resets Mira state to its ready state.
   */
  const setMiraReadyState = () => {
    setMiraState(() => ({
      ...miraReadyState,
    }));
  };

  /**
   * Initiates process generation by setting the required parameters.
   * @param {Object} processData - Data related to the target process.
   * @param {string} processData.targetProcess - The type of process to generate.
   * @param {Object} processData.platformContext - Context data required for the process.
   * @param {Object} [config] - Optional configuration.
   * @param {number} [config.pollingDelay=0] - Delay in ms before starting polling.
   * @throws {Error} - If input parameters are invalid.
   */
  const generate = (processData, config = {}) => {
    const { targetProcess, platformContext } = processData;
    if (!targetProcess || !platformContext) {
      throw new Error("Invalid input: targetProcess and platformContext are required.");
    }

    const currentId =
      miraState.processData?.cacheHashId || miraState.processData?.platformContext?.resourceId;
    const incomingId = processData.cacheHashId || processData.platformContext?.resourceId;

    if (
      miraState.processStatus !== "ready" &&
      miraState.targetProcess === targetProcess &&
      currentId === incomingId
    ) {
      console.error(`Generation already in progress for ${currentId}`);
      return;
    }

    setMiraState(() => ({
      ...miraReadyState,
      targetProcess,
      error: false,
      processData, //enables useGenerateProcess
      pollingDelay: config.pollingDelay || 0,
      pollingDelayComplete: false,
    }));
  };

  // Handles the polling delay for process status polling
  useEffect(() => {
    // If invocationId is not set, there is no process to poll
    if (!miraState.invocationId) return;

    if (miraState.pollingDelay === 0) {
      setMiraState((prev) => ({ ...prev, pollingDelayComplete: true }));
      return;
    }

    const t = setTimeout(() => {
      setMiraState((prev) => ({ ...prev, pollingDelayComplete: true }));
    }, miraState.pollingDelay);

    return () => clearTimeout(t);
  }, [miraState.invocationId, miraState.pollingDelay]);

  useGenerateProcess({
    enabled: miraState.targetProcess != "" && miraState.invocationId === null,
    processData: miraState.processData, // this is the body sent to /v3/process/generate
    onSuccess: ({ data }) => {
      const status = parseProcessStatus(data);
      if (MiraProcessFailedStates.includes(status)) {
        setMiraState(() => ({
          ...miraReadyState,
          error: true,
        }));
      } else {
        setMiraState((prevProps) => ({
          ...prevProps,
          processIsComplete: MiraProcessSuccessStates.includes(status),
          invocationId: data?.invocation_id,
          generationAttemptId: data?.generation_attempt_id || null,
          correlationReceipt: data?.correlation_receipt || null,
          processStatus: status !== "unknown" ? status : "in_progress",
          error: false,
        }));
      }
    },
    onError: () => {
      console.error("Generate process failed");
      setMiraState(() => ({
        ...miraReadyState, // reset ready state to enable a subsequent generate() call if necessary after an error
        error: true,
      }));
    },
  });

  // Polls for the process status and updates the state accordingly.
  useProcessStatusPolling({
    invocationId:
      miraState.pollingDelayComplete && !miraState.processIsComplete
        ? miraState.invocationId
        : null,
    onSuccess: ({ data }) => {
      const status = parseProcessStatus(data);
      if (status === "completed") {
        setMiraState((prevProps) => ({
          ...prevProps,
          processIsComplete: true, // Enables useGetProcess
        }));
      } else if (status === "failed") {
        console.error("Async process failed");
        setMiraState(() => ({
          ...miraReadyState,
          error: true,
        }));
      }
    },
    onError: () => {
      console.error("Process polling failed");
      setMiraState(() => ({
        ...miraReadyState,
        error: true,
      }));
    },
  });

  // retrieves the process result once polling is complete
  useGetProcessResult({
    invocationId: miraState.invocationId,
    generationAttemptId: miraState.generationAttemptId,
    correlationReceipt: miraState.correlationReceipt,
    enabled: miraState.processIsComplete,
    targetProcess: miraState.targetProcess,
    onSuccess: async ({ data }) => {
      if (miraState.processData.platformContext.responseType === "zip") {
        let blobData = new Blob([data], { type: "application/zip" });

        const downloadLink = document.createElement("a");
        const url = URL.createObjectURL(blobData);

        downloadLink.href = url;
        downloadLink.download = "session-notes.zip";
        downloadLink.style.display = "none";

        document.body.appendChild(downloadLink);
        downloadLink.click();

        setTimeout(() => {
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
        }, 200);
        setMiraState(() => ({
          ...miraReadyState,
          response: "zip_file_download",
        }));
      } else {
        setMiraState((prevProps) => ({
          ...miraReadyState,
          response: data?.result || data,
          invocationId: prevProps.invocationId,
          generationAttemptId: prevProps.generationAttemptId,
          correlationReceipt: prevProps.correlationReceipt,
          targetProcess: prevProps.targetProcess,
          processData: prevProps.processData,
        }));
      }
    },

    onError: () => {
      console.error("Process result retrieval failed");
      setMiraState(() => ({
        ...miraReadyState, // reset ready state to enable a subsequent generate() call if necessary after an error
        error: true,
      }));
    },
  });

  /**
   * Updates user input in GlobalMira component.
   * @param {string} input - The user input.
   */
  const handleUserInput = (input) => {
    setGlobalMiraProps((prevProps) => ({
      ...prevProps,
      userInput: input,
    }));
  };

  /**
   * Allows users to modify the response content.
   * @param {string} input - The modified response.
   */
  const handleOutputChange = (input) => {
    setMiraState((prevProps) => ({
      ...prevProps,
      response: input,
    }));
  };

  // Render the Provider with the mira context value and the GlobalMira component
  return (
    <MiraContext.Provider
      value={{
        showMira,
        hideMira,
        generate,
        miraState,
        setMiraReadyState,
      }}
    >
      {children}
      <GlobalMira
        open={globalMiraProps.open}
        output={miraState.response}
        error={miraState.error}
        retryHandler={globalMiraProps.retryHandler}
        buttonText={globalMiraProps.buttonText}
        userInput={globalMiraProps.userInput}
        handleUserInput={handleUserInput}
        onPress={generate}
        onClose={hideMira}
        showCopyToClipboard={globalMiraProps.copyToClipboard}
        customButton={globalMiraProps.customButton}
        showTextArea={globalMiraProps.showTextArea}
        showOutputArea={globalMiraProps.showOutputArea}
        handleOutputChange={handleOutputChange}
      />
    </MiraContext.Provider>
  );
};
